import { motion } from "framer-motion";
import { Eye, Users, BookOpen } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const offerings = [
  {
    icon: Eye,
    title: "Organisationsentwickler, Coaches und Supervisor:innen",
    description:
      "Wer andere bewegt, braucht selbst einen festen Stand. Wir bieten Fall-Supervision für Beratende und Coaches an, um die eigene Professionalität zu schärfen und blinde Flecken zu klären.",
  },
  {
    icon: Users,
    title: "Teams",
    description:
      "Gemeinsam innehalten. Wir helfen Teams dabei, Dynamiken zu verstehen, emotionale Lasten abzubauen und die Arbeitsfähigkeit auch in Hochdruckphasen oder Krisen zu sichern.",
  },
  {
    icon: BookOpen,
    title: "Lehr- und Ausbilder:innen",
    description:
      "Fachliche Begleitung für Menschen in der Ausbildung. Wir unterstützen Sie dabei, Ihren eigenen Beratungsstil zu finden und die hohen Standards zertifizierter Verbände zu erfüllen.",
  },
];

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Supervision",
  description: "Professionelle Supervision und Reflexion für Organisationsentwickler:innen, Coaches, Teams und Lehr- und Ausbilder:innen. Fallsupervision und Qualitätssicherung.",
  provider: {
    "@type": "Organization",
    name: "Leadership Focus GmbH",
    url: "https://leadership-focus.de",
  },
  areaServed: [
    { "@type": "Country", name: "Deutschland" },
    { "@type": "Country", name: "Österreich" },
    { "@type": "Country", name: "Schweiz" },
  ],
  serviceType: "Supervision",
};

const Supervision = () => (
  <>
    <SEOHead title="Supervision – Leadership Focus" description="Professionelle Supervision und Reflexion für Fachkräfte und Teams. Leadership Focus München." canonical="https://leadership-focus.de/portfolio/supervision" />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }} />
    <Header />
    <main className="py-20 md:py-28">
      <div className="container">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center mb-14"
        >
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            Supervision
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Wir schaffen den Raum, um mit Distanz auf das eigene Handeln zu blicken.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {offerings.map((item) => (
            <motion.div key={item.title} variants={fadeInUp}>
              <Card className="h-full border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-2 font-serif">{item.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </main>
    <Footer />
  </>
);

export default Supervision;
